/**
 * AssignSubcontractorPanel
 * Inline panel for assigning subcontractors from within any modal.
 * Opens as a slide-in panel inside the parent modal.
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface AvailableSub {
  id: string;
  full_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
}

interface AssignedSub {
  id: string;
  full_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  member_role: string;
  access_scope: string | null;
}

interface Props {
  projectId: string;
  onAssigned: (sub: AssignedSub) => void;
  onCancel: () => void;
}

export default function AssignSubcontractorPanel({ projectId, onAssigned, onCancel }: Props) {
  const [available, setAvailable] = useState<AvailableSub[]>([]);
  const [alreadyAssigned, setAlreadyAssigned] = useState<AssignedSub[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Load available subs + already assigned subs
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');

      try {
        // 1. Get already assigned subs for this project (via edge function)
        const { data: listData, error: listErr } = await supabase.functions.invoke('jphph-subcontractor-assignments', {
          body: { action: 'list', project_id: projectId },
        });

        if (cancelled) return;

        if (listErr) {
          console.error('[AssignPanel] list error:', listErr);
          setError('Could not load current subcontractors.');
        } else {
          setAlreadyAssigned((listData?.subcontractors as AssignedSub[]) || []);
        }

        // 2. Get ALL available subcontractor profiles (via edge function)
        const { data: allData, error: allErr } = await supabase.functions.invoke('jphph-subcontractor-assignments', {
          body: { action: 'all', project_id: projectId },
        });

        if (cancelled) return;

        if (allErr) {
          console.error('[AssignPanel] all error:', allErr);
          setError((prev) => prev || 'Could not load available subcontractors.');
        } else {
          const allSubs = (allData?.subcontractors as AvailableSub[]) || [];
          // Filter out already assigned
          const assignedIds = new Set((listData?.subcontractors || []).map((s: AssignedSub) => s.id));
          setAvailable(allSubs.filter((s) => !assignedIds.has(s.id)));
        }
      } catch (e) {
        if (!cancelled) {
          console.error('[AssignPanel] load exception:', e);
          setError('Something went wrong loading subcontractors.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [projectId]);

  const handleAssign = useCallback(async () => {
    if (!selectedUserId) return;
    setSaving(true);
    setError('');

    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('jphph-subcontractor-assignments', {
        body: {
          action: 'assign',
          project_id: projectId,
          user_id: selectedUserId,
          member_role: 'subcontractor',
          access_scope: 'standard',
        },
      });

      if (invokeErr) {
        console.error('[AssignPanel] assign error:', invokeErr);
        setError(invokeErr.message || 'Assignment failed.');
        setSaving(false);
        return;
      }

      if (data?.alreadyAssigned) {
        // Was already assigned — just treat as success
        const sub = available.find((s) => s.id === selectedUserId);
        if (sub) {
          onAssigned({ ...sub, member_role: 'subcontractor', access_scope: 'standard' });
        }
        return;
      }

      if (data?.success) {
        const sub = available.find((s) => s.id === selectedUserId);
        if (sub) {
          onAssigned({ ...sub, member_role: 'subcontractor', access_scope: 'standard' });
        }
      } else {
        setError(data?.error || 'Assignment failed.');
      }
    } catch (e) {
      console.error('[AssignPanel] assign exception:', e);
      setError('Something went wrong.');
    } finally {
      setSaving(false);
    }
  }, [selectedUserId, projectId, available, onAssigned]);

  const filtered = available.filter((s) => {
    const term = searchTerm.toLowerCase();
    return (
      (s.full_name || '').toLowerCase().includes(term) ||
      (s.company_name || '').toLowerCase().includes(term) ||
      (s.email || '').toLowerCase().includes(term)
    );
  });

  return (
    <div className="bg-[#F9FAFB] rounded-xl border border-gray-200 p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#E8621A]/10 flex items-center justify-center">
            <i className="ri-user-add-line text-[#E8621A] text-sm"></i>
          </div>
          <div>
            <p className="text-sm font-bold text-[#1A1A1A]">Assign Subcontractor</p>
            <p className="text-[10px] text-[#9CA3AF]">Select an existing subcontractor to assign to this project</p>
          </div>
        </div>
        <button onClick={onCancel} className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
          <i className="ri-close-line text-[#374151] text-sm"></i>
        </button>
      </div>

      {/* Already assigned list */}
      {alreadyAssigned.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Already assigned to this project</p>
          <div className="flex flex-wrap gap-1.5">
            {alreadyAssigned.map((s) => (
              <div key={s.id} className="inline-flex items-center gap-1.5 bg-[#ECFDF5] border border-[#A5F3FC] rounded-lg px-2.5 py-1">
                <i className="ri-tools-line text-[#059669] text-[10px]"></i>
                <span className="text-[11px] font-medium text-[#059669]">
                  {s.full_name || s.company_name || s.email || 'Subcontractor'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search + List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <i className="ri-loader-4-line animate-spin text-[#E8621A] text-xl"></i>
          <span className="text-sm text-[#9CA3AF] ml-2">Loading subcontractors...</span>
        </div>
      ) : error ? (
        <div className="bg-[#FEF2F2] rounded-lg p-3 border border-[#FECACA]">
          <p className="text-xs text-[#EF4444]">{error}</p>
        </div>
      ) : available.length === 0 ? (
        <div className="text-center py-6">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 text-[#D1D5DB]">
            <i className="ri-user-search-line text-3xl"></i>
          </div>
          <p className="text-sm font-semibold text-[#374151]">No available subcontractors</p>
          <p className="text-xs text-[#9CA3AF] mt-1">All subcontractors are already assigned to this project.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, company or email..."
            className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]"
          />

          <div className="max-h-[240px] overflow-y-auto space-y-1.5 pr-1">
            {filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelectedUserId(s.id === selectedUserId ? '' : s.id)}
                className={`w-full text-left rounded-xl border p-3 cursor-pointer transition-colors flex items-center gap-3 ${
                  selectedUserId === s.id
                    ? 'border-[#E8621A] bg-[#FEF0E7]/30'
                    : 'border-gray-100 bg-white hover:border-gray-200'
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${selectedUserId === s.id ? 'bg-[#E8621A]/10' : 'bg-gray-100'}`}>
                  <i className={`ri-tools-line ${selectedUserId === s.id ? 'text-[#E8621A]' : 'text-[#9CA3AF]'}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1A1A1A] truncate">
                    {s.full_name || s.company_name || 'Unnamed Subcontractor'}
                  </p>
                  {s.company_name && s.full_name && (
                    <p className="text-[10px] text-[#9CA3AF]">{s.company_name}</p>
                  )}
                  {s.email && (
                    <p className="text-[10px] text-[#9CA3AF] truncate">{s.email}</p>
                  )}
                </div>
                {selectedUserId === s.id && (
                  <div className="w-5 h-5 rounded-full bg-[#E8621A] flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white text-[10px]"></i>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Assign button */}
          <button
            onClick={handleAssign}
            disabled={!selectedUserId || saving}
            className="w-full py-2.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin"></i>Assigning...</>
            ) : (
              <><i className="ri-user-add-line"></i>Assign to Project</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}